// ==================== Anti-Scraping: Client Attestation ====================
// Collects passive environment signals (headless-browser / automation
// indicators) and reports them once per tab session to the backend via
// POST /security/attest. This is strictly fire-and-forget: every failure is
// swallowed silently — attestation must NEVER affect UX.

import { apiClient } from './api';

const ATTESTED_KEY = 'aime_attested';

// Run fn, returning null when it throws or yields undefined — each signal is
// guarded individually so one blocked API can't poison the rest.
function safeSignal<T>(fn: () => T): T | null {
    try {
        const value = fn();
        return value === undefined ? null : value;
    } catch {
        return null;
    }
}

// UNMASKED WebGL vendor/renderer via WEBGL_debug_renderer_info on a throwaway
// canvas. Headless environments typically report SwiftShader / llvmpipe /
// Mesa OffScreen here even when the user agent looks like desktop Chrome.
function collectWebGL(): { vendor: string | null; renderer: string | null } {
    let vendor: string | null = null;
    let renderer: string | null = null;
    try {
        const canvas = document.createElement('canvas');
        const gl = (canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
        if (gl) {
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (debugInfo) {
                vendor = safeSignal(() => String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)));
                renderer = safeSignal(() => String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)));
            }
            // Release the throwaway context immediately
            safeSignal(() => gl.getExtension('WEBGL_lose_context')?.loseContext());
        }
    } catch {
        // ignore — vendor/renderer stay null
    }
    return { vendor, renderer };
}

function collectSignals(): Record<string, unknown> {
    const webgl = collectWebGL();
    return {
        webdriver: safeSignal(() => navigator.webdriver ?? null),
        has_chrome: safeSignal(() => !!(window as any).chrome),
        plugins_length: safeSignal(() => navigator.plugins.length),
        languages: safeSignal(() => Array.prototype.slice.call(navigator.languages || [])),
        webgl_vendor: webgl.vendor,
        webgl_renderer: webgl.renderer,
        user_agent: safeSignal(() => navigator.userAgent),
        hardware_concurrency: safeSignal(() => navigator.hardwareConcurrency ?? null),
        device_memory: safeSignal(() => (navigator as any).deviceMemory ?? null),
        max_touch_points: safeSignal(() => navigator.maxTouchPoints ?? null),
        cookie_enabled: safeSignal(() => navigator.cookieEnabled),
        screen_width: safeSignal(() => window.screen.width),
        screen_height: safeSignal(() => window.screen.height),
        color_depth: safeSignal(() => window.screen.colorDepth),
        // Scored server-side (_score_signals): a real browser reports a real
        // DPR; its absence is a headless tell. Must be sent or dpr_missing is
        // true for every client (universal +10 noise floor).
        device_pixel_ratio: safeSignal(() => window.devicePixelRatio ?? null),
        // Headless browsers frequently report 0 for the outer window
        outer_width: safeSignal(() => window.outerWidth),
        outer_height: safeSignal(() => window.outerHeight),
        timezone: safeSignal(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    };
}

/**
 * Fire-and-forget attestation. Runs at most once per tab session
 * (sessionStorage guard) and swallows ALL errors silently.
 */
export function attestOnce(): void {
    try {
        if (sessionStorage.getItem(ATTESTED_KEY)) {
            return;
        }
        sessionStorage.setItem(ATTESTED_KEY, '1');
        const signals = collectSignals();
        apiClient.post('/security/attest', { signals }).catch(() => {
            // Swallow — attestation must never surface to the user
        });
    } catch {
        // Swallow everything (sessionStorage blocked, etc.)
    }
}
